import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { selectCurrentUserUUID } from "Reducers/app/selectors";
import { postUserVerification } from "Api/users/postUserVerification.request";

import { fetchVerificationType } from "./fetchVerificationType.thunk";

export type SubmitUserVerificationProps = {
  organizationId: string;
  token: string;
};

export type SubmitUserVerificationResult = {
  canCreate: boolean;
  message?: string;
};

/**
 * Submits a Stripe payment token (pm_*) to the user verification endpoint, then
 * refetches "can create" so the caller can tell whether verification cleared.
 *
 * The card is verified server-side via the dedicated verification endpoint
 * rather than being attached as an organization payment source. Attaching a
 * rejected payment source and then attempting project creation surfaced a 403,
 * so we now gate project creation on the refreshed `can_create`.
 */
export const submitUserVerification = createAppAsyncThunk(
  "onboarding/submitUserVerification",
  async (
    { organizationId, token }: SubmitUserVerificationProps,
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const userId = selectCurrentUserUUID(getState());

      await postUserVerification(userId, { type: "credit-card", token });

      // Refetch can-create so the verification gating state reflects the result.
      const { canCreate, message } = await dispatch(
        fetchVerificationType({ organizationId, isRefreshing: true })
      ).unwrap();

      const result: SubmitUserVerificationResult = {
        canCreate: canCreate ?? false,
        message
      };

      return result;
    } catch (err: unknown) {
      const error = err instanceof Error ? err.message : "An error occurred";

      logger(err, {
        action: "submitUserVerification",
        organizationId
      });

      return rejectWithValue({ error });
    }
  }
);
